import * as THREE from "three";
import React, { useRef, useEffect, memo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Text } from '@react-three/drei';
import Control from '../store/control';
import { TubeCurve } from '../shape/Spline';

import { drawIntersectionPoints,
          getContours } from './utils';

import { 
  calculateCurveCenter,
  findClosestPointOnPoints,
  intersectionCurvePlane
} from "../shape/utils";


const getIntersectCurve360 = (curvePoints) => {
  const points = [];
  for (let angle = 0; angle < 360; angle += 20) {
    //console.log('-------angle------------', angle)
    const radians = THREE.MathUtils.degToRad(angle);
    const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
    
    const curve = new THREE.CatmullRomCurve3(curvePoints);
    const center = calculateCurveCenter(curve);
    const plane = new THREE.Plane(normal, -normal.dot(center));

    // calculate curve radius from curve length
    const offsetScalarRefPoint = 0.9 * curve.getLength() / (2 * Math.PI);


    var refPoint = normal.clone().multiplyScalar(offsetScalarRefPoint);
    // rotation matrix to rotate around y axis to make the normal vector of the plane to be (0, 0, 1)
    var rotationMatrix = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);
    // apply rotation
    refPoint.applyMatrix4(rotationMatrix);
    refPoint = refPoint.add(center);

    var intersectionPoints = intersectionCurvePlane(curve, plane, 300);

    if (intersectionPoints.length == 0 || intersectionPoints.length == 1) continue;

    // if cross section plane height is same as side hole, there are 1 cross section curve with inner and outer surface.
    // and plane angle is same as side hole, there are 2 cross section curve with inner and outer surface, but they are same direction, and there are nopoints with opposite direction.
    if (intersectionPoints.length == 2) {
      const oppositeRadians = THREE.MathUtils.degToRad(angle+180);
      const oppositeNormal = new THREE.Vector3(Math.cos(oppositeRadians), 0, Math.sin(oppositeRadians));
      var oppositeRefPoint = oppositeNormal.clone().multiplyScalar(offsetScalarRefPoint);
      oppositeRefPoint.applyMatrix4(rotationMatrix);
      oppositeRefPoint = oppositeRefPoint.add(center);

      // console.log(intersectionPoints)
      // console.log(refPoint)
      // console.log(oppositeRefPoint)
      // console.log(intersectionPoints[0].distanceTo(refPoint))
      // console.log(intersectionPoints[0].distanceTo(oppositeRefPoint))

      // judge intersection points are close to refPoint or oppositeRefPoint
      if (intersectionPoints[0].distanceTo(refPoint) > intersectionPoints[0].distanceTo(oppositeRefPoint)) {

        continue;
      } else {
        //continue;
      }
    }

    // if cross section plane height is same as side hole, there are 1 cross section curve with inner and outer surface.
    // so the number of intersection points is 4, 2 is inner surface with opposite direction, 2 is outer surface with opposite direction.
    if (intersectionPoints.length == 4) {
      const group1 = [];
      const group2 = [];

      const closestPoint = findClosestPointOnPoints(intersectionPoints.slice(1,4), intersectionPoints[0]);
      group1.push(intersectionPoints[0]);
      group1.push(closestPoint.point);

      // add two points, which are not in group1, to group2
      for (let i = 1; i < intersectionPoints.length; i++) {
        if (intersectionPoints[i].equals(closestPoint.point, 1)) continue;
        group2.push(intersectionPoints[i]);
      }

      const closestPointGroup1toCenter = findClosestPointOnPoints(group1, center);
      const closestPointGroup2toCenter = findClosestPointOnPoints(group2, center);
      intersectionPoints = [closestPointGroup1toCenter.point, closestPointGroup2toCenter.point];

      // console.log(group1, group2)
    }

    const closestPoint = findClosestPointOnPoints(intersectionPoints, refPoint);

    // console.log(points.length)
    // console.log(refPoint)
    // console.log(intersectionPoints)
    // console.log(closestPoint.point)
    
    points.push(closestPoint.point);
  }

  // add first point to last point to make a closed curve
  points.push(points[0]);

  return points;
}

const deletePointsByDistance = (points) => {
  // Calculate the average of the distance between two points
  var sum = 0;
  for (let i = 0; i < points.length-1; i++) {
    sum += points[i].distanceTo(points[i+1]);
  }
  const aveDistance = sum / (points.length-1);
  var threshold = (aveDistance*10 > 30) ? 30 : aveDistance*10;

  // If the distance between two points is greater than the threshold, delete that point
  for (let i = 0; i < points.length-1; i++) {
    if (points[i].distanceTo(points[i+1]) > threshold) {
      points.splice(i+1, 1);
    }
  }
  return points;
}


const MSoftInsert = memo(({
  meshLeg,
  meshSocketLoad,
  prosthetics,
  topLineHeight,
  bottomLineHeight,
  offsetToBottom,
  color,
  colorHigh,
  colorSub
}) => {
  const meshTarget = (prosthetics === 'BK') ? meshLeg : meshSocketLoad;
  const objects = [];

  const refGroup  = useRef();
  var key = 0;

  const topLinePoints = [];
  const bottomLinePoints = [];
  const overlap = 10;

  meshSocketLoad.geometry.computeBoundingBox();
  const minY = meshSocketLoad.geometry.boundingBox.min.z;
  const maxY = meshSocketLoad.geometry.boundingBox.max.z;
  let minYLeg = null;
  if (prosthetics === 'BK') {
    minYLeg = meshLeg.geometry.boundingBox.min.y;
  }
  const showOffset = (prosthetics === 'BK') ? meshSocketLoad.geometry.boundingBox.max.x + 10 :  meshSocketLoad.geometry.boundingBox.max.y + 10;


  // get contour at topLineHeight
  const topOffset = minY+topLineHeight

  var plane = new THREE.Plane(new THREE.Vector3(0,1,0), -topOffset);
  try {
    var vertices = drawIntersectionPoints(meshTarget, plane, topOffset, false);
  } catch (e) {
    console.log(e);

    return null;
  }


  var topContours = getContours(vertices, [], true);
  var topContour = topContours[0];
  topContour = deletePointsByDistance(topContour);
  if (prosthetics === 'AK') {
    topContour = getIntersectCurve360(topContour);
  }
  topContour = new THREE.CatmullRomCurve3(topContour).getSpacedPoints(20);

  const lengthTopContourCurve = new THREE.CatmullRomCurve3(topContour, true).getLength() + overlap;

  objects.push(<TubeCurve
    key={key} 
    vpoints={topContour} 
    radius={0.5}
    color={colorHigh}
  />)
  key += 1;



  if (prosthetics === 'BK') {
    topLinePoints.push(new THREE.Vector3(showOffset, topOffset, -lengthTopContourCurve/2));
    topLinePoints.push(new THREE.Vector3(showOffset, topOffset, lengthTopContourCurve/2));
  } else {
    topLinePoints.push(new THREE.Vector3(-lengthTopContourCurve/2, topOffset, showOffset));
    topLinePoints.push(new THREE.Vector3(lengthTopContourCurve/2, topOffset, showOffset));
  }

  objects.push(<TubeCurve
    key={key} 
    vpoints={topLinePoints} 
    radius={0.5} 
    color={colorHigh}
  />)
  key += 1;





  // get contour at bottomLineHeight
  let bottomOffset = 0;
  if (prosthetics === 'BK') {
    bottomOffset = minYLeg+bottomLineHeight
  } else if (prosthetics === 'AK') {
    bottomOffset = minY+bottomLineHeight
  }

  var plane = new THREE.Plane(new THREE.Vector3(0,1,0), -bottomOffset);
  try {
    var vertices = drawIntersectionPoints(meshTarget, plane, bottomOffset, false);
  } catch (e) {
    console.log(e);
    return null;
  }

  try {
    var bottomContours = getContours(vertices, [], true);
    var bottomContour = bottomContours[0];
    bottomContour = deletePointsByDistance(bottomContour);
    if (prosthetics === 'AK') {
      bottomContour = getIntersectCurve360(bottomContour);
    }
    bottomContour = new THREE.CatmullRomCurve3(bottomContour).getSpacedPoints(20);

    const lengthBottomContourCurve = new THREE.CatmullRomCurve3(bottomContour, true).getLength() + overlap;

    objects.push(<TubeCurve
      key={key} 
      vpoints={bottomContour} 
      radius={0.5} 
      color={colorHigh}
    />)
    key += 1;

    if (prosthetics === 'BK') {
      bottomLinePoints.push(new THREE.Vector3(showOffset, bottomOffset, -lengthBottomContourCurve/2));
      bottomLinePoints.push(new THREE.Vector3(showOffset, bottomOffset, lengthBottomContourCurve/2));
    } else if (prosthetics === 'AK') {
      bottomLinePoints.push(new THREE.Vector3(-lengthBottomContourCurve/2, bottomOffset, showOffset));
      bottomLinePoints.push(new THREE.Vector3(lengthBottomContourCurve/2, bottomOffset, showOffset));
    }

    objects.push(<TubeCurve
      key={key} 
      vpoints={bottomLinePoints} 
      radius={0.5} 
      color={colorHigh}
    />)
    key += 1;




    // make trapezoid
    let leftLinePoints = [];
    let rightLinePoints = [];
    let angleLeftLine = 0;
    let bottomExtension = 0;
    if (prosthetics === 'BK') {
      angleLeftLine = Math.atan((bottomLinePoints[0].z - topLinePoints[0].z) / (bottomLinePoints[0].y - topLinePoints[0].y));
    } else if (prosthetics === 'AK') {
      angleLeftLine = Math.atan((bottomLinePoints[0].x - topLinePoints[0].x) / (bottomLinePoints[0].y - topLinePoints[0].y));
    }
    
    
    if (prosthetics === 'BK') {
      bottomExtension = (bottomLineHeight) / Math.cos(angleLeftLine);
    } else if (prosthetics === 'AK') {
      bottomExtension = -offsetToBottom / Math.cos(angleLeftLine);
    }
    const topExtension = (60 + maxY - (topLineHeight + minY)) / Math.cos(angleLeftLine); // 60 is the offset from the top of the socket

    leftLinePoints.push(bottomLinePoints[0].clone(), topLinePoints[0].clone());
    rightLinePoints.push(bottomLinePoints[1].clone(), topLinePoints[1].clone());

    // line extension
    var direction = new THREE.Vector3().subVectors(leftLinePoints[1], leftLinePoints[0]).normalize();
    leftLinePoints[0].addScaledVector(direction, -bottomExtension);
    leftLinePoints[1].addScaledVector(direction, topExtension);

    // line extension
    var direction = new THREE.Vector3().subVectors(rightLinePoints[1], rightLinePoints[0]).normalize();
    rightLinePoints[0].addScaledVector(direction, -bottomExtension);
    rightLinePoints[1].addScaledVector(direction, topExtension);
    

    objects.push(<TubeCurve
      key={key} 
      vpoints={leftLinePoints} 
      radius={1} 
      color={colorHigh}
    />)
    key += 1;

    objects.push(<TubeCurve
      key={key} 
      vpoints={rightLinePoints} 
      radius={1} 
      color={colorHigh}
    />)
    key += 1;

    // draw line between left and right on top
    const topExtensionPoints = [];
    topExtensionPoints.push(leftLinePoints[1].clone(), rightLinePoints[1].clone());
    const distTop = topExtensionPoints[0].distanceTo(topExtensionPoints[1]);

    objects.push(<TubeCurve
      key={key} 
      vpoints={topExtensionPoints} 
      radius={1} 
      color={colorHigh}
    />)
    key += 1;

    objects.push(<Text
      key={key} 
      position={(prosthetics=='BK') ? 
        [topExtensionPoints[0].x, topExtensionPoints[0].y-10, topExtensionPoints[0].z-10] : 
        [topExtensionPoints[1].x, topExtensionPoints[1].y-10, topExtensionPoints[1].z-10]
      }
      rotation={(prosthetics=='BK') ? [0, Math.PI/2, 0] : [0, 0, 0]}
      color={'white'}
      fontSize={20}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      {Math.round(distTop)} mm
    </Text>)
    key += 1;

    // draw line between left and right on bottom
    const bottomExtensionPoints = [];
    bottomExtensionPoints.push(leftLinePoints[0].clone(), rightLinePoints[0].clone());
    const distBottom = bottomExtensionPoints[0].distanceTo(bottomExtensionPoints[1]);

    objects.push(<TubeCurve
      key={key} 
      vpoints={bottomExtensionPoints} 
      radius={1} 
      color={colorHigh}
    />)
    key += 1;

    objects.push(<Text
      key={key} 
      position={(prosthetics=='BK') ? 
        [bottomExtensionPoints[0].x, bottomExtensionPoints[0].y-10, bottomExtensionPoints[0].z-10] : 
        [bottomExtensionPoints[1].x, bottomExtensionPoints[1].y-10, bottomExtensionPoints[1].z-10]
      }
      rotation={(prosthetics=='BK') ? [0, Math.PI/2, 0] : [0, 0, 0]}
      color={'white'}
      fontSize={20}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      {Math.round(distBottom)} mm
    </Text>)
    key += 1;

    // draw center line
    const centerLinePoints = [];
    centerLinePoints.push(leftLinePoints[0].clone().add(rightLinePoints[0]).divideScalar(2));
    centerLinePoints.push(leftLinePoints[1].clone().add(rightLinePoints[1]).divideScalar(2));
    const distCenter = centerLinePoints[0].distanceTo(centerLinePoints[1]);

    objects.push(<TubeCurve
      key={key} 
      vpoints={centerLinePoints} 
      radius={1} 
      color={colorSub}
    />)
    key += 1;

    objects.push(<Text
      key={key} 
      position={[(centerLinePoints[0].x+centerLinePoints[1].x)/2, (centerLinePoints[0].y+centerLinePoints[1].y)/2, (centerLinePoints[0].z+centerLinePoints[1].z)/2]}
      rotation={(prosthetics=='BK') ? [0, Math.PI/2, 0] : [0, 0, 0]}
      color={colorSub}
      fontSize={20}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      {Math.round(distCenter)} mm
    </Text>)
    key += 1;

    objects.push(<Text
      key={key} 
      position={[centerLinePoints[0].x, centerLinePoints[0].y-10, centerLinePoints[0].z]}
      rotation={(prosthetics=='BK') ? [0, Math.PI/2, 0] : [0, 0, 0]}
      color={'white'}
      fontSize={20}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      {Math.round(distBottom/2)} mm
    </Text>)
    key += 1;

    objects.push(<Text
      key={key} 
      position={[centerLinePoints[1].x, centerLinePoints[1].y+10, centerLinePoints[1].z]}
      rotation={(prosthetics=='BK') ? [0, Math.PI/2, 0] : [0, 0, 0]}
      color={'white'}
      fontSize={20}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      {Math.round(distTop/2)} mm
    </Text>)
    key += 1;
  } catch (e) {
    console.log(e);
  }

  return objects.length !== 0 ? (
    <group ref={refGroup}>
      {objects}
    </group>
  ) : null;
});

export default MSoftInsert;
